import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'

import { useToggleStaticElements } from '../../../hooks/useToggleStaticElement'
import { QuoteContext } from '../QuoteContext'

type AnyElement = Element | Element[] | NodeListOf<Element>

interface PortalContextProps {
  /**
   * The root element this component will get rendered into
   */
  rootElement?: HTMLElement

  /**
   * Static (server rendered) element associated with this component.
   * Hide this element when the component gets rendered
   */
  staticElement?: HTMLElement

  toggleStaticElement: (isEnabled?: boolean) => void
}

export const QuotePortalContext = React.createContext<PortalContextProps>({
  rootElement: undefined,
  staticElement: undefined,
  toggleStaticElement: () => {},
})

/**
 * Portal component props
 */
interface QuotePortalProps {
  /**
   * Override the ticker index from context if you need to skip the first ticker
   */
  index?: number

  /**
   * Server rendered elements to replace with react components
   */
  staticElements?: AnyElement

  /**
   * Immediately replace the static element with react children.
   * Turn this off if the child component is suspended
   *
   * @default true (if root exists)
   */
  toggleStaticElement?: boolean

  /**
   * Elements to render the react components into
   */
  roots: AnyElement

  /**
   * Intentionally restricted to JSX element so that we don’t have to check children is valid react element
   */
  children: JSX.Element
}

export function QuotePortal({ index, toggleStaticElement, staticElements, roots, children }: QuotePortalProps) {
  // We might need to offset the index if we want to skip the first ticker (eg. static image chart)
  const context = React.useContext(QuoteContext)
  const elementIndex = index ?? context.index

  // Get the root and associated static html element, get them by index if array
  const staticElement =
    staticElements instanceof NodeList || Array.isArray(staticElements) ? staticElements[elementIndex] : staticElements
  const rootElement = roots instanceof NodeList || Array.isArray(roots) ? roots[elementIndex] : roots

  /**
   * Set up element switching. We might want to hold off if we’re passing a suspendable component.
   * We can’t catch the promise here, it might have already been caught down the tree
   * and we want to avoid hiding the server-rendered element before the react content is ready
   */
  const [isEnabled, setIsEnabled] = React.useState<boolean>(toggleStaticElement ?? (!!staticElement && !!rootElement))
  useToggleStaticElements(staticElement, rootElement, isEnabled)

  const portalContextValue = React.useMemo(
    () => ({
      // Cast here to make it easier to pass any `Element`s as props
      rootElement: rootElement as HTMLElement,
      staticElement: staticElement as HTMLElement,
      // A way to get notified when children get rendered, because they could be suspended and waiting for requests
      toggleStaticElement: (enabled?: boolean) => setIsEnabled((prevValue) => enabled ?? !prevValue),
    }),
    [rootElement, staticElement]
  )

  if (!rootElement) return null

  return (
    <ErrorBoundary fallback={null} onError={() => setIsEnabled(false)}>
      <QuotePortalContext.Provider value={portalContextValue}>
        {ReactDOM.createPortal(children, rootElement)}
      </QuotePortalContext.Provider>
    </ErrorBoundary>
  )
}
